import React from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';

export const PrivacyPolicy = () => {
  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
            <p className="text-sm text-gray-600 mb-8">Last Updated: November 27, 2025</p>

            <div className="prose prose-lg max-w-none">
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
                <p className="text-gray-700 mb-4">
                  NetAuctions.Inc ("we," "us," or "our") respects your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our Service.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Information We Collect</h2>
                
                <h3 className="text-xl font-semibold text-gray-900 mb-3">2.1 Information You Provide</h3>
                <p className="text-gray-700 mb-4">We collect information you voluntarily provide when you:</p>
                <ul className="list-disc pl-6 text-gray-700 mb-4">
                  <li>Create an account or claim a listing</li>
                  <li>Request to be listed in our directory</li>
                  <li>Submit a quote request</li>
                  <li>Subscribe to Premier listing services</li>
                  <li>Contact us for support</li>
                </ul>
                <p className="text-gray-700 mb-4">This may include:</p>
                <ul className="list-disc pl-6 text-gray-700 mb-4">
                  <li>Name and professional credentials</li>
                  <li>License number and issuing state</li>
                  <li>Contact information (email, phone, address)</li>
                  <li>Business information (company name, website, specialties)</li>
                  <li>Payment information (processed securely through third-party providers)</li>
                  <li>Profile content (bio, photos, logos, videos)</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 mb-3">2.2 Automatically Collected Information</h3>
                <p className="text-gray-700 mb-4">When you access our Service, we automatically collect:</p>
                <ul className="list-disc pl-6 text-gray-700 mb-4">
                  <li>IP address and approximate geographic location</li>
                  <li>Browser type and version</li>
                  <li>Device information</li>
                  <li>Pages visited and time spent on pages</li>
                  <li>Referring website addresses</li>
                  <li>Search queries within our Service</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 mb-3">2.3 Cookies and Tracking Technologies</h3>
                <p className="text-gray-700 mb-4">
                  We use cookies, web beacons, and similar technologies to enhance user experience, analyze usage patterns, and deliver personalized content. You can control cookie preferences through your browser settings.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">3. How We Use Your Information</h2>
                <p className="text-gray-700 mb-4">We use collected information to:</p>
                <ul className="list-disc pl-6 text-gray-700 mb-4">
                  <li>Provide, maintain, and improve our Service</li>
                  <li>Create and manage user accounts</li>
                  <li>Process Premier listing subscriptions and payments</li>
                  <li>Facilitate communication between clients and auctioneers</li>
                  <li>Display your listing information to potential clients</li>
                  <li>Send transactional emails (account updates, quote requests)</li>
                  <li>Provide customer support</li>
                  <li>Verify professional licenses with state boards</li>
                  <li>Detect and prevent fraud or abuse</li>
                  <li>Analyze usage patterns and improve user experience</li>
                  <li>Comply with legal obligations</li>
                  <li>Send marketing communications (with your consent)</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Information Sharing and Disclosure</h2>
                
                <h3 className="text-xl font-semibold text-gray-900 mb-3">4.1 Public Information</h3>
                <p className="text-gray-700 mb-4">
                  Information in your public listing (name, license number, contact information, specialties, photos, videos) is visible to all Service users and may appear in search engine results.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 mb-3">4.2 Service Providers</h3>
                <p className="text-gray-700 mb-4">
                  We share information with third-party service providers who assist with:
                </p>
                <ul className="list-disc pl-6 text-gray-700 mb-4">
                  <li>Payment processing (Stripe)</li>
                  <li>Email delivery</li>
                  <li>Cloud hosting and storage</li>
                  <li>Analytics and performance monitoring</li>
                  <li>Customer support tools</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 mb-3">4.3 Legal Requirements</h3>
                <p className="text-gray-700 mb-4">
                  We may disclose your information if required by law, court order, or government request, or to protect our rights, property, or safety.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 mb-3">4.4 Business Transfers</h3>
                <p className="text-gray-700 mb-4">
                  In the event of a merger, acquisition, or sale of assets, your information may be transferred to the acquiring entity.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Data Security</h2>
                <p className="text-gray-700 mb-4">
                  We implement industry-standard security measures to protect your information, including:
                </p>
                <ul className="list-disc pl-6 text-gray-700 mb-4">
                  <li>Encryption of data in transit (SSL/TLS)</li>
                  <li>Secure password hashing</li>
                  <li>Two-factor authentication for account access</li>
                  <li>Regular security audits and updates</li>
                  <li>Access controls and employee training</li>
                </ul>
                <p className="text-gray-700 mb-4">
                  However, no method of transmission over the internet is 100% secure. We cannot guarantee absolute security.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Your Privacy Rights</h2>
                
                <h3 className="text-xl font-semibold text-gray-900 mb-3">6.1 Access and Correction</h3>
                <p className="text-gray-700 mb-4">
                  You may access and update your account information at any time through your dashboard.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 mb-3">6.2 Data Deletion</h3>
                <p className="text-gray-700 mb-4">
                  You may request deletion of your account and associated data by contacting us at admin@net-auctions.com. Some information may be retained for legal or legitimate business purposes.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 mb-3">6.3 Marketing Communications</h3>
                <p className="text-gray-700 mb-4">
                  You may opt out of marketing emails by clicking "unsubscribe" in any marketing message or updating your preferences in your account settings.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 mb-3">6.4 Cookie Management</h3>
                <p className="text-gray-700 mb-4">
                  You can control cookies through your browser settings. Disabling cookies may limit some Service functionality.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 mb-3">6.5 Do Not Track</h3>
                <p className="text-gray-700 mb-4">
                  We currently do not respond to Do Not Track signals from browsers.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Children's Privacy</h2>
                <p className="text-gray-700 mb-4">
                  Our Service is not intended for individuals under 18 years of age. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Third-Party Links</h2>
                <p className="text-gray-700 mb-4">
                  Our Service may contain links to third-party websites (auctioneer websites, review platforms). We are not responsible for the privacy practices of these external sites. We encourage you to review their privacy policies.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">9. International Data Transfers</h2>
                <p className="text-gray-700 mb-4">
                  Your information may be transferred to and processed in countries other than your country of residence. We ensure appropriate safeguards are in place for such transfers.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">10. California Privacy Rights</h2>
                <p className="text-gray-700 mb-4">
                  California residents have additional rights under the California Consumer Privacy Act (CCPA):
                </p>
                <ul className="list-disc pl-6 text-gray-700 mb-4">
                  <li>Right to know what personal information is collected</li>
                  <li>Right to know if personal information is sold or disclosed</li>
                  <li>Right to opt-out of sale of personal information</li>
                  <li>Right to deletion of personal information</li>
                  <li>Right to non-discrimination for exercising CCPA rights</li>
                </ul>
                <p className="text-gray-700 mb-4">
                  Note: We do not sell personal information to third parties.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Data Retention</h2>
                <p className="text-gray-700 mb-4">
                  We retain your information for as long as your account is active or as needed to provide services. We may retain certain information after account closure for legal compliance, dispute resolution, and legitimate business purposes.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Changes to This Privacy Policy</h2>
                <p className="text-gray-700 mb-4">
                  We may update this Privacy Policy periodically. Material changes will be notified via email or prominent notice on our Service. The "Last Updated" date at the top reflects the most recent revision.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Contact Us</h2>
                <p className="text-gray-700 mb-4">
                  For questions about this Privacy Policy or to exercise your privacy rights, contact us at:
                </p>
                <p className="text-gray-700">
                  <strong>Email:</strong> admin@net-auctions.com<br />
                  <strong>Website:</strong> NetAuctions.Inc<br />
                  <strong>Response Time:</strong> We will respond to privacy inquiries within 30 days
                </p>
              </section>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};
